import { useMemo } from 'react';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { MODEL_RUNS_TABLE_ASSETS } from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import { ModelRuns } from '@elementary/common-ui/types';
import { getModelRunsTableAssetsQuery } from '../api';
import { ModelRunsTableAssetsPagedResponse } from '../types';
import { parseTableAssetToModelRuns } from '../utils';
import useModelRunsQueryContext from './useModelRunsQueryContext';

type Props = {
  search?: string;
  enabled?: boolean;
};

const useModelRuns = ({ search, enabled = true }: Props) => {
  const {
    selectedEnvId,
    selectedTreeNodeIdsHash,
    hasEmptyTreeSelection,
    urlFiltersDebounced,
    urlSort,
    sort,
    filters,
    assetFilters,
    filterOptions,
    isFilterOptionsFetching,
  } = useModelRunsQueryContext({ search });

  const { data, isFetching, isLoading, isError, fetchNextPage, hasNextPage } =
    useGraphqlPagedQuery<ModelRuns, ModelRunsTableAssetsPagedResponse>({
      queryKey: [
        MODEL_RUNS_TABLE_ASSETS,
        selectedEnvId,
        selectedTreeNodeIdsHash,
        urlFiltersDebounced,
        urlSort,
        search,
      ],
      query: getModelRunsTableAssetsQuery,
      variables: {
        filters,
        assetFilters: assetFilters.length ? assetFilters : null,
        sort,
        limit: PAGE_SIZE,
        daysBack: DEFAULT_DAYS_BACK,
      },
      select: (response) => ({
        items: response.tableAssets.items.map(parseTableAssetToModelRuns),
        pageInfo: response.tableAssets.pageInfo,
      }),
      enabled: enabled && !hasEmptyTreeSelection,
      envId: selectedEnvId,
    });

  const tableData = useMemo(
    () => (hasEmptyTreeSelection ? [] : data),
    [data, hasEmptyTreeSelection],
  );

  return {
    tableData,
    isTableAssetsFetching: isLoading || isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    filterOptions,
    isFilterOptionsFetching,
  };
};

export default useModelRuns;
